import { inject, Injectable } from '@angular/core';
import type { TimeSeries } from '@clean-code/models';
import { TranslocoService } from '@jsverse/transloco';
import dayjs from 'dayjs';
import PivotGridDataSource from 'devextreme/ui/pivot_grid/data_source';
import { PivotTableService } from './pivot-table.service';

@Injectable({
  providedIn: 'root',
})
export class PivotTableConfigService {
  translateService = inject(TranslocoService);
  private pivotTableService = inject(PivotTableService);

  dataSourceConfig(data: TimeSeries[]): PivotGridDataSource {
    const dataSourceConfig = new PivotGridDataSource({
      store: data,
      fields: [
        {
          caption: this.translateService.translate('raw-data.fields.DATE'),
          dataField: 'tradeDate',
          area: 'row',
          dataType: 'date',
          groupInterval: 'day',
          sortOrder: 'desc',
          selector: this.dateSelector,
          width: 100,
          isMeasure: false,
          customizeText: (e) => {
            if (e.value !== undefined && e.value !== null) {
              e.valueText = dayjs(e.value).format('DD.MM.YYYY');
            } else {
              e.valueText = '';
            }
            return e.valueText;
          },
        },
        {
          caption: this.translateService.translate(
            'raw-data.filters.IDENTIFIER',
          ),
          dataField: 'identifier',
          area: 'column',
          sortingMethod: (
            (dataArr: TimeSeries[]) =>
            (
              a: { value?: string | number },
              b: { value?: string | number },
            ) => {
              // Find objects by identifier to get their key values
              const identifierA = a.value?.toString() || '';
              const identifierB = b.value?.toString() || '';

              const objA = dataArr.find(
                (item) => item.identifier === identifierA,
              );
              const objB = dataArr.find(
                (item) => item.identifier === identifierB,
              );

              if (!objA || !objB) return 0;

              // Parse key format: parserType_timeTick_ricName
              const parseKey = (key: string) => {
                const parts = key.split('_');
                if (parts.length >= 3) {
                  return {
                    parserType: Number.parseInt(parts[0]),
                    timeTick: Number.parseInt(parts[1]),
                    ricName: parts.slice(2).join('_'), // Handle ricNames with underscores
                  };
                }
                return { parserType: 0, timeTick: 0, ricName: key };
              };

              const keyA = parseKey(objA.key);
              const keyB = parseKey(objB.key);

              // Current time tick for comparison (approximate .NET ticks)
              const currentTimeTick = (Date.now() + 621355968000000000) * 10000;

              // Check if contracts are expired (timeTick < current time)
              const isExpiredA = keyA.timeTick < currentTimeTick;
              const isExpiredB = keyB.timeTick < currentTimeTick;

              // Active contracts come first, expired contracts go to the end
              if (!isExpiredA && isExpiredB) return -1;
              if (isExpiredA && !isExpiredB) return 1;

              // Sort by parserType first
              if (keyA.parserType !== keyB.parserType) {
                return keyA.parserType - keyB.parserType;
              }

              // Within same parserType and expiry status, sort by timeTick then ricName
              if (keyA.timeTick !== keyB.timeTick) {
                return keyA.timeTick - keyB.timeTick;
              }

              // Finally sort by ricName
              return keyA.ricName.localeCompare(keyB.ricName);
            }
          )(data),
          dataType: 'string',
          selector: this.symbolSelector,
          isMeasure: false,
        },

        {
          caption: this.translateService.translate(
            'raw-data.fields.SETTLEMENT_PRICE',
          ),
          dataField: 'settlementPrice',
          summaryType: 'sum',
          area: 'data',
          isMeasure: true,
          format: 'decimal',
          customizeText: (e) => {
            if (e.value !== undefined && e.value !== null) {
              e.valueText = this.formatNumber(e.value);
            } else {
              e.valueText = '';
            }
            return e.valueText;
          },
        },
        {
          caption: this.translateService.translate(
            'raw-data.fields.OPEN_PRICE',
          ),
          dataField: 'openPrice',
          summaryType: 'sum',
          area: null,
          isMeasure: true,
          format: 'decimal',
          customizeText: (e) => {
            if (e.value !== undefined && e.value !== null) {
              e.valueText = this.formatNumber(e.value);
            } else {
              e.valueText = '';
            }
            return e.valueText;
          },
        },
        {
          caption: this.translateService.translate(
            'raw-data.fields.HIGH_PRICE',
          ),
          dataField: 'highPrice',
          summaryType: 'sum',
          isMeasure: true,
          format: 'decimal',
          customizeText: (e) => {
            if (e.value !== undefined && e.value !== null) {
              e.valueText = this.formatNumber(e.value);
            } else {
              e.valueText = '';
            }
            return e.valueText;
          },
        },
        {
          caption: this.translateService.translate('raw-data.fields.LOW_PRICE'),
          dataField: 'lowPrice',
          summaryType: 'sum',
          isMeasure: true,
          format: 'decimal',
          customizeText: (e) => {
            if (e.value !== undefined && e.value !== null) {
              e.valueText = this.formatNumber(e.value);
            } else {
              e.valueText = '';
            }
            return e.valueText;
          },
        },
        {
          caption: this.translateService.translate('raw-data.fields.VOLUME'),
          dataField: 'volume',
          summaryType: 'sum',
          isMeasure: true,
          format: 'decimal',
          customizeText: (e) => {
            if (e.value !== undefined && e.value !== null) {
              e.valueText = this.formatNumber(e.value);
            } else {
              e.valueText = '';
            }
            return e.valueText;
          },
        },
        {
          dataField: 'id',
          visible: false,
        },
        {
          dataField: 'isIntraday',
          visible: false,
        },
        {
          dataField: 'unit',
          visible: false,
        },
        {
          dataField: 'timestamp',
          visible: false,
        },
        {
          dataField: 'tradeDate',
          visible: false,
        },
      ],
      onChanged: () => {
        this.pivotTableService.setPivotTableData(
          dataSourceConfig.getAreaFields('data', false),
        );
      },
    });
    return dataSourceConfig;
  }

  symbolSelector(data: TimeSeries) {
    if (data.name) {
      return `${data.name}, ${data.identifier}, ${data.unit}`;
    }
    return `${data.identifier}, ${data.unit}`;
  }
  dateSelector(data: TimeSeries) {
    return dayjs(data.tradeDate).format('YYYY-MM-DD');
  }

  formatNumber(value: number | string | Date) {
    let numericValue: number;
    if (typeof value === 'string') {
      numericValue = Number(value);
    } else if (value instanceof Date) {
      numericValue = value.valueOf();
    } else {
      numericValue = value;
    }
    return Number(numericValue).toLocaleString('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
}
